/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react'
import { Typography, useTheme } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import {
  getActionLoading,
  getEditableSurvey,
  getSurveySections,
  getSurveyTitle
} from '../../store/slices/SurveyTemplateSlice/selectors'
import { type ISurveySection } from '../../store/slices/SurveyTemplateSlice'
import { notification } from '../../components/Notifications'
import { checkQuestionIsDirtyAgainstBaseline, findInvalidQuestion, findInvalidSection, scrollToElement } from '../../features/SurveyTempleteFeature/helpers'
import _ from 'lodash'
import { type ISurveySection as ITemplateSurveySection } from '../../models/SurveyTemplate'
import { StyledSaveMenuWrapper } from '../../features/SurveyTempleteFeature/SurveyHeader/styles'
import { Messages, type Question, SurveyItemQuestionType } from '../../features/SurveyTempleteFeature/types'
import { useMutation } from '@tanstack/react-query'
import { ADDED_QUESTION_TYPES } from '../../consts/addedQuestionTypes'
import { AutoImportManager } from '../../APIManager'
import GenericModal from '../../components/GenericModal'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'

/**
 * User-facing name for the shared set, deliberately *not* `ADDED_QUESTION_TYPES.SECTION`. That
 * constant is the wire and search-param value ("Section-added Questions"), and it was being
 * persisted as the section's `name` — which the save handler reads straight back into
 * `${name} added successfully`. Every other surface in this feature calls the type "Course Section"
 * (the switcher tab, the card title, the settings label), so the constant leaked a name no label
 * uses into a toast the instructor sees.
 */
const SHARED_SET_DISPLAY_NAME = 'Course Section Questions'

const SurveyHeader: React.FC = (): JSX.Element => {
  const theme = useTheme()
  const navigate = useNavigate()
  const surveyTitle = getSurveyTitle()
  const surveySections = getSurveySections()
  const editableSurvey = getEditableSurvey()

  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const slotId = params.get('slotId') ?? ''
  const divisionId = params.get('divisionId') ?? ''
  const type = params.get('type')
  const categoryName = params.get('categoryName') ?? ''
  const termId = params.get('termId') ?? ''
  const isLoading = getActionLoading()
  const [isConfirmingSharedDelete, setIsConfirmingSharedDelete] = useState(false)
  const checkIsTitleDirty = (): boolean => {
    if (surveyTitle !== editableSurvey.title) return true
    return false
  }

  const checkIsSectionDirty = (section: ISurveySection, sectionIndex: number): boolean => {
    const areAllSectionEqual = _.isEqual(editableSurvey?.surveySections?.[sectionIndex], section)
    return !areAllSectionEqual
  }

  const checkIsSectionIsOld = (section: ISurveySection): boolean => {
    const t = editableSurvey.surveySections.find((s: ITemplateSurveySection) => s.surveySectionId === section.surveySectionId)
    if (t?.surveySectionId) {
      return true
    }
    return false
  }

  const checkQuestionIsDirty = (sectionIndex: number, newQuestion: Question): boolean => {
    return checkQuestionIsDirtyAgainstBaseline(
      editableSurvey?.surveySections?.[sectionIndex]?.surveyItems,
      newQuestion
    )
  }

  const { mutate: saveQuestion, isPending } = useMutation({
    mutationFn: async (payload: any) => {
      // CR-295: the section's shared set. Same payload shape as the other two — the backend runs
      // it through the same `create_survey_section` — so only the endpoint differs. Writes are
      // Published-only on their side (400 otherwise), and the caller must be an instructor on the
      // section, which is why this branch is never reached from the read-only variant.
      if (type === ADDED_QUESTION_TYPES.SECTION) {
        return editableSurvey
          ? await AutoImportManager.patchSectionAddedQuestions(slotId, divisionId, payload)
          : await AutoImportManager.postSectionAddedQuestions(slotId, divisionId, payload)
      }
      if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
        return editableSurvey
          ? await AutoImportManager.patchAllInstructorQuestions(slotId, divisionId, payload)
          : await AutoImportManager.postAllInstructorQuestions(slotId, divisionId, payload)
      }
      return editableSurvey
        ? await AutoImportManager.patchAllAdminQuestions(slotId, divisionId, payload)
        : await AutoImportManager.postAllAdminQuestions(slotId, divisionId, payload)
    },
    onSuccess: (response) => {
      const message = response?.data?.name ? `${response.data.name} added successfully` : 'Questions added successfully'
      notification(message, { variant: 'success' })
      // The section type is reached from the instructor dashboard, same as the instructor type.
      if (type === ADDED_QUESTION_TYPES.INSTRUCTOR || type === ADDED_QUESTION_TYPES.SECTION) {
        navigate(`${ROUTES.INSTRUCTOR_SURVEYS}?termId=${termId ?? ''}`)
      } else {
        navigate(`${ROUTES.ADMIN_VIEWER_DASHBOARD}?termId=${termId ?? ''}`)
      }
    },
    onError: (err: any) => {
      notification(err?.message, { variant: 'error' }
      )
    }
  })

  const submitEditableSurvey = async (): Promise<void> => {
    const isTitleDirty = checkIsTitleDirty()
    const areAllSectionsEqual = _.isEqual(surveySections, editableSurvey?.surveySections)
    const isTemplateDirty = isTitleDirty || !areAllSectionsEqual

    let preparedTitle = ''
    if (!type) {
      preparedTitle = `${categoryName} added Questions`
    } else if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
      preparedTitle = 'Instructor-added Questions'
    } else if (type === ADDED_QUESTION_TYPES.SECTION) {
      preparedTitle = SHARED_SET_DISPLAY_NAME
    }
    const preparedData = {
      title: surveyTitle,
      isDirty: isTemplateDirty,
      surveySections: surveySections?.map((section: ISurveySection, sectionIndex: number) => ({
        isDirty: checkIsSectionIsOld(section) ? checkIsSectionDirty(section, sectionIndex) : undefined,
        name: preparedTitle,
        isRepeatPerInstructor: section.isRepeatPerInstructor,
        title: section.title ?? undefined,
        descr: section.descr,
        surveySectionId: section.surveySectionId ?? undefined,
        attributes: section.attributes,
        branchingFilters: section.branchingFilters,
        indentation: section.indentation,
        parentSurveySectionId: section.parentSurveySectionId,
        revision: section.revision,
        surveyItems: section.surveyItems?.map((question: Question) => ({
          isDirty: question.itemId && checkQuestionIsDirty(sectionIndex, question),
          itemId: question.itemId ?? undefined,
          itemType: question.itemType,
          prompt: question.prompt,
          isRequired: question.isRequired,
          revision: question.revision,
          isSchoolApproved: question?.isSchoolApproved,
          branchingFilters: question?.branchingFilters,
          ...(question.itemType === SurveyItemQuestionType.MULTIPLE_CHOICE ||
            question.itemType === SurveyItemQuestionType.LINEAR_SCALE ||
            question.itemType === SurveyItemQuestionType.MULTI_SELECT
            ? { object: question.object }
            : {})
        }))
      }))
    }
    saveQuestion(preparedData.surveySections[0])
  }

  const submitCreateSurvey = async (): Promise<any> => {
    let preparedName = surveyTitle
    if (!type) {
      preparedName = `${categoryName} added Questions`
    } else if (type === ADDED_QUESTION_TYPES.INSTRUCTOR) {
      preparedName = 'Instructor-added Questions'
    } else if (type === ADDED_QUESTION_TYPES.SECTION) {
      preparedName = SHARED_SET_DISPLAY_NAME
    }
    const preparedSection = {
      ...surveySections[0],
      name: preparedName
    }
    saveQuestion(preparedSection)
  }

  const submitSurvey = async (): Promise<void> => {
    const submit = editableSurvey ? submitEditableSurvey : submitCreateSurvey
    await submit()
  }

  /**
   * Saving the shared set with nothing in it **deletes it for every instructor on the section**.
   *
   * That is the backend's convention rather than an accident — `PATCH .../section_template/` with an
   * empty `survey_items` clears the node's pointer (`slot_added_question_utils.py:908`), and there is
   * no DELETE route. It is the delete counterpart of the shared edit: one instructor removes the
   * questions and they are gone for all of them, with no per-instructor copy to fall back on.
   *
   * So it confirms first. Only for this type, and only when a set already exists — an empty save on
   * the instructor's own set removes only their own, and an empty save with no set yet creates
   * nothing worth warning about.
   */
  // Counted the way the rest of the screen counts questions — TITLE rows excluded. The raw
  // `surveyItems.length` treated a set holding nothing but a leftover title row as non-empty, so the
  // save skipped this confirmation and cleared the shared set without asking.
  // `DESCRIPTIVE_TEXT` here is the same wire value ('Title') that the builder's own filter calls
  // `TITLE` — this file imports the enum from `SurveyTempleteFeature/types`, which names that member
  // differently from the one in `models/SurveyTemplate`.
  const sharedQuestionCount = surveySections?.[0]?.surveyItems?.filter(
    (item: Question) => item.itemType !== SurveyItemQuestionType.DESCRIPTIVE_TEXT
  ).length ?? 0
  const willDeleteSharedSet = type === ADDED_QUESTION_TYPES.SECTION &&
    Boolean(editableSurvey) &&
    sharedQuestionCount === 0

  const handleSave = async (): Promise<void> => {
    if (willDeleteSharedSet) {
      setIsConfirmingSharedDelete(true)
      return
    }
    await submitSurvey()
  }

  useEffect(() => {
    findInvalidSection(surveySections)
  }, [surveySections])

  return (
    <React.Fragment>
      {isConfirmingSharedDelete && (
        <GenericModal
          isOpen
          onClose={() => { setIsConfirmingSharedDelete(false) }}
          title="Remove the shared questions?"
          maxWidth="sm"
          action={false}
        >
          <Typography fontSize="14px" mb={2}>
            Saving with no questions removes this course section&apos;s shared set. Every instructor
            on the section loses these questions — the set is shared, so there is no per-instructor
            copy left behind.
          </Typography>
          <DefaultModalActions
            destructiveButtonProps={{
              onClick: () => { setIsConfirmingSharedDelete(false) },
              disabled: isPending
            }}
            confirmButtonProps={{
              onClick: () => {
                setIsConfirmingSharedDelete(false)
                void submitSurvey()
              },
              text: 'Remove Questions',
              loading: isPending
            }}
          />
        </GenericModal>
      )}
      <StyledSaveMenuWrapper>
        <GenericButton
          buttonType={ButtonType.Primary}
          buttonSize={ButtonSize.Medium}
          loading={isLoading || isPending}
          label={editableSurvey ? 'Save' : 'Add'}
          onClick={() => {
            void handleSave()
          }}
          style={{
            flexShrink: 0,
            [theme.breakpoints.down('md')]: {
              width: '100%'
            }
          }}
          disabled={false}
        />
      </StyledSaveMenuWrapper>

    </React.Fragment>
  )
}
export default SurveyHeader
